import { useNavigate } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import { Compass, PlayCircle, ArrowRight, Quotes } from "@phosphor-icons/react";

export default function FounderStory() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#0D1B2A] vv-grain" data-testid="founder-story-page">
      <PublicHeader />

      <main className="max-w-2xl mx-auto px-5 sm:px-6 py-14 sm:py-20">
        {/* Founder's seal */}
        <img
          src="/founder_seal.png"
          alt="Vintage Vantage founder's seal — established 2026"
          data-testid="founder-story-seal"
          className="w-24 h-24 sm:w-28 sm:h-28 object-contain mb-8 drop-shadow-sm"
        />
        {/* Eyebrow + headline */}
        <p className="label-caps text-[#B47A3C] mb-6" style={{ letterSpacing: "0.22em" }} data-testid="founder-story-eyebrow">
          Founder's Story · Vintage Vantage
        </p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-medium tracking-tight" data-testid="founder-story-title">
          I didn't set out to build another marketplace.
        </h1>
        <p className="mt-7 text-xl sm:text-2xl leading-relaxed text-[#0D1B2A]/80" data-testid="founder-story-lead">
          I set out to solve a problem I had been living for more than five years as a seller of high-end vintage furniture.
        </p>

        <div className="flex items-center gap-4 my-12">
          <span className="h-px flex-1 bg-[#E5E0D6]" />
          <Compass size={20} weight="fill" className="text-[#B47A3C] opacity-70" />
          <span className="h-px flex-1 bg-[#E5E0D6]" />
        </div>

        {/* Body */}
        <div className="space-y-6 text-[17px] sm:text-lg leading-[1.8] text-[#0D1B2A]/85" data-testid="founder-story-body">
          <p className="first-letter:font-serif-display first-letter:text-6xl first-letter:leading-[0.8] first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:text-[#B47A3C]">
            Every listing I created represented hours of work — researching provenance, taking photographs, recording measurements, writing descriptions, pricing pieces, and building the trust buyers need before making a significant purchase online. Like thousands of other sellers, I assumed that work would become one of my greatest business assets.
          </p>

          <p className="font-serif-display text-2xl sm:text-3xl leading-snug text-[#0D1B2A] py-2">
            Instead, I discovered that much of it couldn't meaningfully travel with me.
          </p>

          <p>
            When I exported my inventory, I received little more than a skeleton spreadsheet containing titles, SKUs, and prices. The knowledge I had spent years creating remained scattered across individual listings, making it difficult to reuse my own work or efficiently expand to other marketplaces. I realized the problem wasn't my inventory — it was that the value I'd already created wasn't portable.
          </p>

          <p className="font-serif-display text-2xl sm:text-3xl text-[#B47A3C] py-2">
            That realization became Vintage Vantage.
          </p>

          <p>
            Vintage Vantage helps sellers recover the work they've already done, transforming limited marketplace exports into a complete, seller-owned inventory. From there, AI helps identify new finds, organize listing details, build complete drafts, and publish to multiple marketplaces from a single source of truth. Instead of recreating listings over and over, sellers continue building on the work they've already invested.
          </p>

          <p>
            Along the way, I discovered something unexpected about artificial intelligence. At its best, AI doesn't replace human expertise — it amplifies it. The goal was never to remove the seller from the process. The goal was to eliminate repetitive work so sellers can spend more time sourcing, restoring, curating, and growing their business.
          </p>
        </div>

        {/* Pull quote */}
        <blockquote className="my-12 border-l-2 border-[#B47A3C] pl-6 sm:pl-8" data-testid="founder-story-pullquote">
          <Quotes size={30} weight="fill" className="text-[#B47A3C]/40 mb-3" />
          <p className="font-serif-display text-2xl sm:text-3xl leading-snug tracking-tight text-[#0D1B2A]">
            I believe sellers should own more than their inventory. They should own the value created by every hour they've invested building it.
          </p>
        </blockquote>

        {/* Belief — the signature line */}
        <div className="my-12 bg-[#0D1B2A] text-[#FDFBF7] px-7 sm:px-10 py-10 sm:py-12 text-center vv-grain rounded-sm" data-testid="founder-story-belief">
          <p className="label-caps text-[#B47A3C] mb-5" style={{ letterSpacing: "0.22em" }}>One simple belief</p>
          <p className="font-serif-display text-2xl sm:text-4xl leading-[1.15] tracking-tight">
            You already did the hard part. Our job is to make sure you never have to do it again.
          </p>
        </div>

        <div className="space-y-6 text-[17px] sm:text-lg leading-[1.8] text-[#0D1B2A]/85">
          <p>
            When sellers own their inventory, their knowledge, and the freedom to use both wherever opportunity exists, everyone benefits. Sellers gain independence, marketplaces receive better listings, buyers receive better information, and the entire vintage industry becomes stronger.
          </p>
          <p className="font-serif-display text-2xl sm:text-3xl text-[#0D1B2A] pt-2">
            That's the future I'm building with Vintage Vantage.
          </p>
        </div>

        <p className="mt-8 label-caps text-[#8A867D]" style={{ letterSpacing: "0.18em" }} data-testid="founder-story-signature">
          — The Founder, Vintage Vantage
        </p>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center gap-3 mt-14 pt-10 border-t border-[#E5E0D6]" data-testid="founder-story-cta">
          <button
            onClick={() => navigate("/register")}
            data-testid="founder-story-get-started"
            className="w-full sm:w-auto h-13 px-7 py-3 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-semibold text-[17px] inline-flex items-center justify-center gap-2 hover:bg-black transition-colors active:scale-95"
          >
            Get started free <ArrowRight size={18} weight="bold" />
          </button>
          <button
            onClick={() => navigate("/?tour=1")}
            data-testid="founder-story-watch"
            className="w-full sm:w-auto h-13 px-7 py-3 border border-[#0D1B2A] rounded-none font-medium inline-flex items-center justify-center gap-2 hover:bg-[#F5F3ED] transition-colors"
          >
            <PlayCircle size={18} weight="fill" className="text-[#B47A3C]" /> Watch the story
          </button>
        </div>
      </main>
    </div>
  );
}
